import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2, Paperclip, Eye, Download, Share2, Bot, Clock, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { appInboxStatusOptions, appInboxSetAppStatus, appInboxAttachmentBlob } from "@/lib/api";

const _statusOptionsCache = {};   // region → { options: [], groups: [{label, options}] }
export const loadStatusOptions = async () => {
  const key = localStorage.getItem("sf_region") === "AUS" ? "AUS" : "UK";
  if (!_statusOptionsCache[key]) {
    try { _statusOptionsCache[key] = await appInboxStatusOptions(); }
    catch { return { options: [], groups: [] }; }
  }
  return _statusOptionsCache[key];
};

export const INTENT_TONE = {
  "New App":                "bg-green-100 text-green-800 border-green-300",
  "Top Up":                 "bg-emerald-100 text-emerald-800 border-emerald-300",
  "Info Requested":         "bg-blue-100 text-blue-800 border-blue-300",
  "Final Checks":           "bg-indigo-100 text-indigo-800 border-indigo-300",
  "Open Banking":           "bg-cyan-100 text-cyan-800 border-cyan-300",
  "Invoice":                "bg-amber-100 text-amber-800 border-amber-300",
  "Chase/Status Update":    "bg-orange-100 text-orange-800 border-orange-300",
  "Broker Intro":           "bg-purple-100 text-purple-800 border-purple-300",
  "Cancellation/Withdrawn": "bg-red-100 text-red-800 border-red-300",
  "Spam/Promo":             "bg-slate-100 text-slate-600 border-slate-300",
  "Bounce/Auto-Reply":      "bg-slate-100 text-slate-500 border-slate-300",
  "Other":                  "bg-slate-100 text-slate-600 border-slate-300",
};

export const STATUS_TONE = {
  proposed: "bg-amber-50 text-amber-800 border-amber-300",
  failed:   "bg-red-50 text-red-700 border-red-300",
  executed: "bg-green-50 text-green-700 border-green-300",
  rejected: "bg-slate-100 text-slate-500 border-slate-300",
  stale:    "bg-slate-100 text-slate-400 border-slate-200",
};

export const fmtDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const today = new Date().toDateString() === d.toDateString();
  return today
    ? d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })
    : d.toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
};

export const fmtKb = (b) => (b ? `${Math.round(b / 1024)} KB` : "");

/** Picker hint: the sender attached this file twice (same name ignoring punctuation + same size) — unticked by default. */
export function DuplicateTag({ att }) {
  if (!att?.duplicate_of) return null;
  return (
    <span className="text-[9px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-1 shrink-0"
      title={`Same file as "${att.duplicate_of}" — unticked so it isn't uploaded twice. Tick it if you really want both.`}
      data-testid="attachment-duplicate-tag">duplicate</span>
  );
}

export const fmtUkDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" });
};

export const isPreviewable = (a) => {
  const t = (a.contentType || "").toLowerCase();
  const n = (a.name || "").toLowerCase();
  return t.includes("pdf") || t.startsWith("image/") || t.startsWith("text/")
    || /\.(pdf|png|jpe?g|gif|webp|txt|csv)$/.test(n);
};

export const downloadAttachment = async (messageId, a, fetchBlob = appInboxAttachmentBlob) => {
  try {
    const blob = await fetchBlob(messageId, a.id);
    const url = URL.createObjectURL(blob);
    const el = document.createElement("a");
    el.href = url;
    el.download = (a.name || "file").split(" → ").pop();
    el.click();
    URL.revokeObjectURL(url);
  } catch {
    toast.error("Download failed");
  }
};

export function StatusSelect({ value, onChange, testId, className }) {
  const [data, setData] = useState({ options: [], groups: [] });
  useEffect(() => { loadStatusOptions().then(d => setData(d || { options: [], groups: [] })); }, []);
  const opts = data.options || [];
  const groups = data.groups || [];
  return (
    <select value={value || ""} onChange={(e) => onChange(e.target.value)}
      className={className || "h-7 text-[11px] border rounded px-1.5 bg-white max-w-[190px]"}
      data-testid={testId}>
      <option value="">— keep current status —</option>
      {groups.length > 0
        ? groups.map(g => (
            <optgroup key={g.label} label={g.label}>
              {g.options.map(o => <option key={o} value={o}>{o}</option>)}
            </optgroup>
          ))
        : opts.map(o => <option key={o} value={o}>{o}</option>)}
      {value && !opts.includes(value) && <option value={value}>{value}</option>}
    </select>
  );
}

export function ApplySuggestedBtn({ item }) {
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const sug = item.payload?.suggested_status;
  if (!sug || done || sug === (item.result?.status_set || "")) return null;
  return (
    <Button size="sm" variant="outline" className="h-5 text-[9px] px-1.5 shrink-0 text-blue-700 border-blue-300"
      disabled={busy}
      onClick={async () => {
        setBusy(true);
        try {
          await appInboxSetAppStatus(item.payload.item_id, sug);
          toast.success(`${item.payload.sf || "Item"} status set to '${sug}'`);
          setDone(true);
        } catch (e) { toast.error(e.response?.data?.detail || e.message); }
        finally { setBusy(false); }
      }}
      data-testid={`apply-suggested-${item.queue_id}`}
      title={item.payload?.status_reasoning || ""}>
      {busy ? <Loader2 className="w-2.5 h-2.5 animate-spin" /> : `Set '${sug}'`}
    </Button>
  );
}

export function AttachmentPreviewDialog({ messageId, att, onClose, fetchBlob = appInboxAttachmentBlob }) {
  const [url, setUrl] = useState(null);
  const [blob, setBlob] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    let obj = null;
    let alive = true;
    (async () => {
      try {
        const b = await fetchBlob(messageId, att.id);
        obj = URL.createObjectURL(b);
        if (alive) { setUrl(obj); setBlob(b); }
      } catch {
        if (alive) setErr("Couldn't load the file for preview");
      }
    })();
    return () => { alive = false; if (obj) URL.revokeObjectURL(obj); };
  }, [messageId, att.id]);  // eslint-disable-line react-hooks/exhaustive-deps

  const t = (att.contentType || "").toLowerCase();
  const n = (att.name || "").toLowerCase();
  const isPdf = t.includes("pdf") || n.endsWith(".pdf");
  const isImg = t.startsWith("image/") || /\.(png|jpe?g|gif|webp)$/.test(n);
  const fileName = (att.name || "file").split(" → ").pop();
  const canShare = !!blob && typeof navigator !== "undefined" && typeof navigator.share === "function" && typeof navigator.canShare === "function";
  const share = async () => {
    try {
      const file = new File([blob], fileName, { type: blob.type || att.contentType || "application/octet-stream" });
      if (!navigator.canShare({ files: [file] })) { toast.error("This device can't share this file type"); return; }
      await navigator.share({ files: [file], title: fileName });
    } catch (e) { if (e?.name !== "AbortError") toast.error("Sharing failed"); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-4xl" aria-describedby={undefined} data-testid="attachment-preview-dialog">
        <DialogHeader>
          <DialogTitle className="text-sm flex items-center gap-2 pr-6">
            <Paperclip className="w-4 h-4 text-slate-500 shrink-0" />
            <span className="truncate">{att.name}</span>
            <span className="text-[10px] text-muted-foreground font-normal shrink-0">{fmtKb(att.size)}</span>
          </DialogTitle>
        </DialogHeader>
        {url && (
          <div className="flex items-center gap-2" data-testid="attachment-preview-actions">
            <a href={url} download={fileName}><Button size="sm" variant="outline" className="h-9" data-testid="attachment-preview-download"><Download className="w-3.5 h-3.5 mr-1" />Download</Button></a>
            {canShare && <Button size="sm" variant="outline" className="h-9" onClick={share} data-testid="attachment-preview-share"><Share2 className="w-3.5 h-3.5 mr-1" />Share…</Button>}
          </div>
        )}
        {err ? (
          <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-3">{err}</div>
        ) : !url ? (
          <div className="p-12 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
        ) : isPdf ? (
          <iframe title="attachment-preview" src={url} className="w-full h-[72vh] border rounded bg-white" />
        ) : isImg ? (
          <div className="max-h-[72vh] overflow-auto text-center bg-slate-50 border rounded p-2">
            <img src={url} alt={att.name} className="max-w-full mx-auto" />
          </div>
        ) : (
          <div className="p-8 text-center text-sm text-muted-foreground space-y-3">
            <div>No inline preview for this file type.</div>
            <a href={url} download={(att.name || "file").split(" → ").pop()}>
              <Button size="sm" variant="outline"><Download className="w-3.5 h-3.5 mr-1" />Download {att.name}</Button>
            </a>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function AttachmentActionIcon({ messageId, att, onPreview, fetchBlob = appInboxAttachmentBlob }) {
  if (isPreviewable(att)) {
    return (
      <button type="button" title="Preview"
        onClick={(ev) => { ev.preventDefault(); ev.stopPropagation(); onPreview(att); }}
        className="text-slate-400 hover:text-blue-600 transition shrink-0"
        data-testid={`att-preview-${att.id}`}>
        <Eye className="w-3.5 h-3.5" />
      </button>
    );
  }
  return (
    <button type="button" title="Download"
      onClick={(ev) => { ev.preventDefault(); ev.stopPropagation(); downloadAttachment(messageId, att, fetchBlob); }}
      className="text-slate-400 hover:text-blue-600 transition shrink-0"
      data-testid={`att-download-${att.id}`}>
      <Download className="w-3.5 h-3.5" />
    </button>
  );
}


/**
 * 20 Oct 2026 (Dean + Sales): one pill per email saying whether it is WAITING FOR AUTOMATION or NEEDS A HUMAN —
 * the Mirror rows already had this, the inbox did not. Derived client-side from the Autopilot queue row for the
 * email (if any), the Autopilot mode/schedule and the triage state. Returns null when the email is done.
 */
export function autopilotPill(e, q, { mode, withinHours, nextChangeLabel, autoTriage, pipeActive }) {
  if (e.app || (e.updates_posted || []).length) return null;                       // handled — other badges say so
  if (q) {
    if (q.status === "executed" || q.status === "rejected" || q.status === "stale") return null;
    if (q.status === "failed") return { tone: "rose", text: "Autopilot failed — needs you", title: q.error || q.detail || "" };
    if (q.status === "proposed") {
      if (q.executable === false) {
        const b = (q.blockers || [])[0] || q.detail || "needs a decision";
        return { tone: "amber", text: `Needs you · ${String(b).slice(0, 48)}`, title: (q.blockers || []).join(" · ") || q.detail || "" };
      }
      if (mode !== "auto") return { tone: "amber", text: "Awaiting approval · Autopilot is manual", title: q.detail || "" };
      return { tone: "violet", text: "Autopilot · about to run", title: q.detail || "" };
    }
  }
  if (!e.triage) {
    if (pipeActive) return null;                                                   // the working bar already shows
    return autoTriage ? { tone: "slate", text: "Triage pending", title: "AI triage runs within ~2 min" }
                      : { tone: "amber", text: "Manual — triage is off", title: "" };
  }
  if (mode !== "auto") return { tone: "amber", text: "Manual — Autopilot off", title: "Switch Autopilot on or handle it yourself" };
  if (withinHours === false) return { tone: "slate", text: `Waiting — Autopilot resumes ${nextChangeLabel || "later"}`, title: "" };
  return { tone: "violet", text: "Waiting for Autopilot", title: "Next Autopilot pass picks this up (every ~2 min)" };
}

const PILL_TONE = {
  violet: "bg-violet-50 text-violet-700 border-violet-300",
  amber: "bg-amber-50 text-amber-800 border-amber-300",
  rose: "bg-rose-50 text-rose-700 border-rose-300",
  slate: "bg-slate-100 text-slate-600 border-slate-300",
};

export function AutopilotPill({ pill, testId }) {
  if (!pill) return null;
  return (
    <Badge variant="outline" className={`text-[9px] gap-0.5 ${PILL_TONE[pill.tone] || PILL_TONE.slate}`} title={pill.title} data-testid={testId}>
      {pill.tone === "violet" ? <Bot className="w-2.5 h-2.5" /> : pill.tone === "slate" ? <Clock className="w-2.5 h-2.5" /> : <AlertTriangle className="w-2.5 h-2.5" />}
      {pill.text}
    </Badge>
  );
}
